import { LegalLayout } from './LegalLayout';

export function PrivacyPage() {
  return (
    <LegalLayout title="Privacy Notice" lastUpdated="2026-04-22">
      <p className="lead">
        This Privacy Notice explains how Praxara Ltd ("Praxara", "we") processes
        personal data as a <strong>data controller</strong> (for our own
        corporate and marketing purposes) and as a <strong>data processor</strong>{' '}
        (for customer tenant data). It is issued in accordance with the UK GDPR
        and EU GDPR.
      </p>

      <h2>1. Who we are</h2>
      <p>
        Praxara Ltd, registered in England and Wales. Registered office:
        available on request. Data Protection Officer:{' '}
        <a href="mailto:dpo@praxara.io">dpo@praxara.io</a>.
      </p>

      <h2>2. Data we process as controller</h2>
      <ul>
        <li><strong>Account data:</strong> name, work email, tenant, role.</li>
        <li><strong>Authentication data:</strong> password hash (bcrypt, not recoverable), TOTP secret, MFA backup code hashes, refresh-token hashes, last-login timestamp.</li>
        <li><strong>Session metadata:</strong> IP address, user-agent, approximate city (IP-derived).</li>
        <li><strong>Audit events:</strong> every action on the Service (who did what, when, with what outcome) for 21 CFR Part 11 traceability.</li>
        <li><strong>Billing data:</strong> held by Stripe, our payment processor, under Stripe's own DPA. We receive customer, subscription and invoice metadata only.</li>
        <li><strong>Marketing data:</strong> website analytics, demo-request form submissions.</li>
      </ul>

      <h2>3. Data we process as processor</h2>
      <p>
        Customer tenants upload regulatory, clinical and pharmacovigilance
        documents which may contain personal data (including special-category
        health data for pharmacovigilance). We process this data <strong>only on
        documented instructions from the customer</strong>, under the{' '}
        <a href="/legal/dpa">Data Processing Agreement</a> signed with the
        customer.
      </p>

      <h2>4. Legal bases</h2>
      <ul>
        <li>Performance of the contract — to provide the Service.</li>
        <li>Legitimate interests — security, abuse prevention, product improvement (non-invasive analytics).</li>
        <li>Legal obligation — tax, accounting, regulatory record-keeping.</li>
        <li>Consent — for optional communications; withdrawable at any time.</li>
      </ul>

      <h2>5. International transfers</h2>
      <p>
        All customer data is processed in the European Union (primarily
        Azure West Europe). Where sub-processors operate outside the EU/UK
        (notably US-based AI model providers), we rely on the EU Commission's
        Standard Contractual Clauses + supplementary measures (encryption,
        pseudonymisation where practicable). See our{' '}
        <a href="/legal/subprocessors">sub-processor list</a> for details.
      </p>

      <h2>6. Retention</h2>
      <ul>
        <li>Account data — duration of contract + 30 days.</li>
        <li>Audit logs — 7 years (21 CFR Part 11 + EU GMP Annex 11 expectations).</li>
        <li>Documents + extractions — customer-configurable; default 7 years for GxP-relevant documents, 30 days for transient uploads.</li>
        <li>Backups — rolling 35-day window, after which they are cryptographically erased.</li>
      </ul>

      <h2>7. Your rights</h2>
      <p>
        Under the GDPR you have the right to access, rectify, erase, restrict
        and port your personal data, and to object to certain processing.
        Requests for data concerning a customer tenant should be directed to
        the tenant's administrator in the first instance; Praxara will support
        the administrator to action the request.
      </p>
      <p>
        Submit a request to <a href="mailto:privacy@praxara.io">privacy@praxara.io</a>.
        You also have the right to complain to the UK Information Commissioner's
        Office (<a href="https://ico.org.uk" target="_blank" rel="noreferrer">ico.org.uk</a>)
        or the EU supervisory authority for your country.
      </p>

      <h2>8. Security</h2>
      <p>
        Praxara operates an ISO 27001-aligned information security programme,
        encrypts data at rest (AES-256) and in transit (TLS 1.2+), enforces
        principle-of-least-privilege access, and maintains a tamper-evident
        audit chain. Penetration tests and SOC 2 Type II are on our 2026 roadmap.
      </p>

      <h2>9. Cookies</h2>
      <p>
        See our <a href="/legal/cookies">Cookie Notice</a>. We use strictly
        necessary cookies for session management and optional analytics cookies
        with consent.
      </p>

      <h2>10. Children's data</h2>
      <p>
        The Service is not directed at children. We do not knowingly collect
        data from anyone under 16.
      </p>

      <h2>11. Changes to this notice</h2>
      <p>
        Material changes are notified via email at least 14 days in advance.
      </p>
    </LegalLayout>
  );
}
